import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, InputNumber, Button, Typography, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconArrowBackUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { message } from '../services/toastMessage';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { refundBookingPartially } from '../services/BookingService';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL, CORAL_SOFT_BG, TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { TextArea } = Input;
const { Text } = Typography;

interface PartialRefundSheetProps {
  booking: IBooking;
  open: boolean;
  onClose: () => void;
  onRefunded: (booking: IBooking) => void;
}

/** Fractions of what is still refundable, offered as one-tap chips. */
const QUICK_FRACTIONS = [0.25, 0.5, 1];

/**
 * "Give some of it back, and say why."
 *
 * The host's own action for everything short of cancelling: a machine that
 * turned up a day late, a job that could not be worked, damage both sides
 * settled at less than the whole. The booking survives it -- which is the
 * entire reason this is not the cancel sheet with a number added.
 *
 * Two things are said out loud on this sheet rather than left to be
 * discovered:
 *
 * 1. WHAT IS LEFT. The maximum comes from the server
 *    (booking.refundableRemaining) and not from a subtraction done here, so
 *    the figure the host is shown as their ceiling is literally the one the
 *    endpoint enforces. A second refund on the same booking is a normal
 *    thing to want, and it has to be obvious how much room is left for it.
 * 2. THE COMMISSION. A host being asked to hand money back will reasonably
 *    assume they are eating the platform's cut of it too. They are not --
 *    the commission drops in the same proportion -- and the moment to say so
 *    is while they are deciding the number, not afterwards.
 *
 * Same mobile-sheet / desktop-modal split, and the same drag-only-from-the-
 * handle reasoning, as HostCancelBookingModal: this sheet holds inputs that
 * a whole-sheet drag listener would fight with.
 */
const PartialRefundSheet: React.FC<PartialRefundSheetProps> = ({ booking, open, onClose, onRefunded }) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dragControls = useDragControls();

  // Falls back to the whole price for a booking fetched from an API old
  // enough not to send this yet -- the server re-checks the ceiling either
  // way, so the worst case is a rejected submit rather than an over-refund.
  const remaining = booking.refundableRemaining ?? booking.totalPrice;
  const currency = booking.hostCurrencyCode;

  const formatMoney = useMemo(
    () => (value: number) => `${value.toFixed(2)} ${currency}`,
    [currency]
  );

  const handleClose = () => {
    setAmount(null);
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_AMOUNT_REQUIRED));
      return;
    }
    if (!reason.trim()) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_REASON_REQUIRED));
      return;
    }
    setSubmitting(true);
    try {
      const updated = await refundBookingPartially(booking.id, amount, reason.trim());
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_SUCCESS));
      onRefunded(updated);
      handleClose();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  /** The amount field, its ceiling, and the one-tap fractions of it. */
  const amountField = (
    <>
      <Text strong style={{ display: 'block', fontSize: 14, marginBottom: 6 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_AMOUNT_LABEL)}
      </Text>
      <InputNumber
        value={amount}
        onChange={(value) => setAmount(value)}
        min={0}
        max={remaining}
        step={10}
        precision={2}
        addonAfter={currency}
        style={{ width: '100%', borderRadius: 14 }}
      />
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
        {t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_AMOUNT_MAX, { amount: formatMoney(remaining) })}
      </Text>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {QUICK_FRACTIONS.map((fraction) => (
          <Button
            key={fraction}
            size="small"
            onClick={() => setAmount(Number((remaining * fraction).toFixed(2)))}
            style={{ borderRadius: 999, borderColor: token.colorBorder }}
          >
            {`${Math.round(fraction * 100)}%`}
          </Button>
        ))}
      </div>
    </>
  );

  const reasonField = (
    <div style={{ marginTop: 16 }}>
      <Text strong style={{ display: 'block', fontSize: 14, marginBottom: 6 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_REASON_LABEL)}
      </Text>
      <TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_REASON_PLACEHOLDER)}
        maxLength={1000}
        style={{ borderRadius: 16 }}
      />
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_REASON_HELP)}
      </Text>
    </div>
  );

  const commissionNote = (
    <div
      style={{
        marginTop: 14,
        padding: '10px 12px',
        borderRadius: 14,
        background: TEAL_SOFT_BG,
        border: `1px solid ${TEAL}`,
      }}
    >
      <Text style={{ fontSize: 12, color: TEAL }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_COMMISSION_NOTE)}
      </Text>
    </div>
  );

  // Nothing left to give back. Shown rather than hidden: a host who came
  // looking for this needs to be told it already happened, not handed an
  // empty form that fails on submit.
  const nothingLeft = remaining <= 0;
  const body = nothingLeft ? (
    <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_NOTHING_LEFT)}</Text>
  ) : (
    <>
      {amountField}
      {reasonField}
      {commissionNote}
    </>
  );

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="refund-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="refund-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                <span
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: CORAL_SOFT_BG,
                    color: CORAL,
                  }}
                >
                  <IconArrowBackUp size={20} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <Text strong style={{ display: 'block', fontSize: 16 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_MODAL_TITLE)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_MODAL_SUBTITLE)}
                  </Text>
                </div>
              </div>
              {body}
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <Button
                  block
                  style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
                  onClick={handleClose}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
                </Button>
                {!nothingLeft && (
                  <Button
                    type="primary"
                    block
                    loading={submitting}
                    onClick={handleSubmit}
                    style={{ borderRadius: 999 }}
                  >
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_CONFIRM)}
                  </Button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_MODAL_TITLE)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_CONFIRM)}
      okButtonProps={{ danger: true, disabled: nothingLeft }}
      confirmLoading={submitting}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_MODAL_SUBTITLE)}
      </Text>
      {body}
    </Modal>
  );
};

export default PartialRefundSheet;
